import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { eur, formatApiError } from "@/lib/api";
import { Btn } from "@/components/common";
import WaSendDialog, { paidTemplate } from "@/components/foto/WaSendDialog";
import StayGalleries from "@/components/foto/StayGalleries";
import AfterPayDialog from "@/components/foto/AfterPayDialog";
import { Send, Copy, ChevronLeft, ChevronRight, Undo2 } from "lucide-react";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const d = (s) => s.split("-").reverse().join("/").slice(0, 5);
const linkOf = (s) => `${window.location.origin}/soggiorno/${s.token}`;
const ym = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;

function StayRow({ s, galleries, onChange, onSend, onPaid }) {
  const [method, setMethod] = useState("contanti");
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(formatApiError(e)); } };
  const pay = async () => {
    if (!window.confirm(`Segnare pagate le foto di ${s.guest || s.room} (${eur(s.price)})?`)) return;
    try {
      const r = await api.post(`/photo-stays/${s.booking_id}/pay`, { method });
      toast.success("Pagato e registrato in Amministrazione");
      onChange();
      onPaid({ ...s, paid: true, token: r.data.token, method });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unpay = () => window.confirm("Annullare il pagamento? L'incasso viene tolto da Amministrazione e il link smette di funzionare.") && act(() => api.delete(`/photo-stays/${s.booking_id}/pay`), "Pagamento annullato");
  return (
    <div className="py-3 border-t border-slate-100 flex flex-wrap items-center gap-3" data-testid={`stay-row-${s.booking_id}`}>
      <div className="min-w-[180px] flex-1">
        <div className="font-medium text-slate-800">{s.guest || "—"} {s.in_house && <span className="text-[10px] font-bold text-sky-700">IN CASA</span>}</div>
        <div className="text-xs text-slate-500">{d(s.checkin)} → {d(s.checkout)}</div>
      </div>
      {s.paid ? (
        <>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid={`stay-paid-${s.booking_id}`}>pagato ({s.method})</span>
          <StayGalleries s={s} galleries={galleries} onSaved={onChange} />
          <Btn data-testid={`stay-send-${s.booking_id}`} disabled={!s.gallery_ids.length} onClick={() => onSend(s)}><Send size={14} /> Invia link</Btn>
          <Btn variant="secondary" data-testid={`stay-copy-${s.booking_id}`} disabled={!s.gallery_ids.length} onClick={() => { navigator.clipboard.writeText(linkOf(s)); toast.success("Link copiato"); }}><Copy size={14} /></Btn>
          <button onClick={unpay} title="Annulla pagamento" data-testid={`stay-unpay-${s.booking_id}`} className="text-slate-500 hover:text-rose-600 p-2 rounded-md border border-slate-200"><Undo2 size={15} /></button>
        </>
      ) : (
        <>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid={`stay-unpaid-${s.booking_id}`}>non pagato</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="text-sm border border-slate-300 rounded-md px-2 py-1" data-testid={`stay-method-${s.booking_id}`}>
            <option value="contanti">Contanti</option><option value="carta">Carta</option><option value="bonifico">Bonifico</option>
          </select>
          <Btn data-testid={`stay-pay-${s.booking_id}`} onClick={pay}>Pagato {eur(s.price)}</Btn>
        </>
      )}
    </div>
  );
}

export default function StayList() {
  const now = new Date();
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(now.getMonth());
  const [data, setData] = useState(null);
  const [send, setSend] = useState(null);
  const [after, setAfter] = useState(null);
  const load = () => api.get(`/photo-stays?month=${ym(y, m)}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [y, m]); // eslint-disable-line react-hooks/exhaustive-deps
  const shift = (n) => { const t = new Date(y, m + n, 1); setY(t.getFullYear()); setM(t.getMonth()); };
  const rooms = [];
  (data?.stays || []).forEach((s) => { let r = rooms.find((x) => x.id === s.property_id); if (!r) { r = { id: s.property_id, label: `${s.room} · ${s.structure_name}`, items: [] }; rooms.push(r); } r.items.push(s); });
  const paid = (data?.stays || []).filter((s) => s.paid);
  return (
    <div data-testid="stay-list">
      {send && <WaSendDialog g={{ title: send.guest, client_phone: send.guest_phone, price: send.price }} link={linkOf(send)} template={paidTemplate} open onClose={() => setSend(null)} />}
      {after && <AfterPayDialog s={after} galleries={data?.galleries || []} onClose={() => setAfter(null)} onSend={(st) => { setAfter(null); load(); setSend(st); }} />}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => shift(-1)} className="p-2 rounded-md border border-slate-200 bg-white" data-testid="stay-prev"><ChevronLeft size={16} /></button>
        <select value={m} onChange={(e) => setM(Number(e.target.value))} className="text-sm border border-slate-300 rounded-md px-2 py-2 bg-white" data-testid="stay-month">
          {MONTHS.map((n, i) => <option key={n} value={i}>{n}</option>)}
        </select>
        <input type="number" value={y} onChange={(e) => setY(Number(e.target.value) || y)} className="w-24 text-sm border border-slate-300 rounded-md px-2 py-2" data-testid="stay-year" />
        <button onClick={() => shift(1)} className="p-2 rounded-md border border-slate-200 bg-white" data-testid="stay-next"><ChevronRight size={16} /></button>
        {data && <span className="text-sm text-slate-600 ml-2" data-testid="stay-summary"><b className="text-emerald-700">{paid.length} pagate</b> · <b className="text-amber-700">{data.stays.length - paid.length} non pagate</b> · incassato {eur(paid.reduce((a, s) => a + s.price, 0))}</span>}
      </div>
      {!data ? <div className="text-slate-400">Caricamento…</div> : rooms.length === 0 ? (
        <div className="text-slate-400 bg-white border border-slate-200 rounded-xl py-10 text-center" data-testid="stay-empty">Nessuna prenotazione in questo mese</div>
      ) : rooms.map((r) => (
        <section key={r.id} className="bg-white border border-slate-200 rounded-xl px-4 pt-3 pb-1 mb-3" data-testid={`stay-room-${r.id}`}>
          <h3 className="font-semibold text-slate-900">{r.label}</h3>
          {r.items.map((s) => <StayRow key={s.booking_id} s={s} galleries={data.galleries} onChange={load} onSend={setSend} onPaid={setAfter} />)}
        </section>
      ))}
    </div>
  );
}
